/**
 * Slider Y4 Custom Element
 * Transform-based vanilla JS engine for high-performance dragging/swiping.
 */

class HeroSlider extends HTMLElement {
  constructor() {
    super();
    // State
    this.currentIndex = 0;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
    this.autoplayInterval = null;
    this.slideWidth = 0;
    this.isDraggingWasDetected = false;
    this.clickBlockUntil = 0;
    this.dragThreshold = 12;

    // Bindings
    this.isInteractiveElement = this.isInteractiveElement.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.getPositionX = this.getPositionX.bind(this);
    this.getPositionY = this.getPositionY.bind(this);
    this.touchStart = this.touchStart.bind(this);
    this.touchMove = this.touchMove.bind(this);
    this.touchEnd = this.touchEnd.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 200);
  }

  connectedCallback() {
    this.track = this.querySelector('.hero-slider__track');
    this.slides = Array.from(this.querySelectorAll('.hero-slide'));
    this.dotsContainer = this.querySelector('.hero-slider__dots');
    this.prevBtn = this.querySelector('.hero-slider__arrow--prev');
    this.nextBtn = this.querySelector('.hero-slider__arrow--next');

    if (!this.track || this.slides.length === 0) return;

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.speed) * 1000 || 5000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.infinite = this.dataset.infinite === 'true';

    this.init();
  }

  init() {
    this.updateDimensions();
    this.createDots();
    this.addEventListeners();

    // Initial Position
    this.setPositionByIndex();

    if (this.autoplayEnabled) this.startAutoplay();

    // Resize Observer
    window.addEventListener('resize', this.handleResize);

    // Observer for visibility (pause when out of view)
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (this.autoplayEnabled) this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }, { threshold: 0.5 });
    observer.observe(this);
  }

  updateDimensions() {
    this.slideWidth = this.clientWidth;
  }

  handleResize() {
    this.updateDimensions();
    this.setPositionByIndex();
  }

  createDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('hero-slider__dot');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => {
        this.currentIndex = index;
        this.setPositionByIndex();
        this.stopAutoplay();
        this.startAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    });
    this.dots = Array.from(this.querySelectorAll('.hero-slider__dot'));
    this.updateDots();
  }

  addEventListeners() {
    // Touch Events (Passive for performance, rely on touch-action: pan-y in CSS)
    this.track.addEventListener('touchstart', this.touchStart, { passive: true });
    this.track.addEventListener('touchmove', this.touchMove, { passive: false });
    this.track.addEventListener('touchend', this.touchEnd);
    this.track.addEventListener('touchcancel', this.touchEnd);

    // Mouse Events
    this.track.addEventListener('mousedown', this.touchStart);
    this.track.addEventListener('mousemove', this.touchMove);
    this.track.addEventListener('mouseup', this.touchEnd);
    this.track.addEventListener('mouseleave', () => {
      if (this.isDragging) this.touchEnd();
    });

    // Prevent Context Menu
    this.track.addEventListener('contextmenu', e => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    // Arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prevSlide();
        this.stopAutoplay();
        this.startAutoplay();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
        this.stopAutoplay();
        this.startAutoplay();
      });
    }

    // Hover Pause
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled) this.startAutoplay();
      });
    }

    // Links in slides
    // Block only the synthetic click produced by the same swipe/drag gesture.
    // This prevents mobile buttons from staying "stuck" after a swipe.
    this.addEventListener('click', this.handleClick, true);
  }

  isInteractiveElement(element) {
    return Boolean(element && element.closest('a, button, input, select, textarea, label, summary, [data-no-slider-drag]'));
  }

  handleClick(event) {
    const link = event.target.closest('a');
    if (!link || !this.contains(link)) return;

    const shouldBlockClick = this.isDraggingWasDetected || Date.now() < this.clickBlockUntil;
    if (!shouldBlockClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDraggingWasDetected = false;
    this.clickBlockUntil = 0;
  }

  getPositionX(event) {
    if (event.type.includes('mouse')) return event.pageX;
    return event.touches && event.touches.length ? event.touches[0].pageX : this.startPos;
  }

  getPositionY(event) {
    if (event.type.includes('mouse')) return event.pageY;
    return event.touches && event.touches.length ? event.touches[0].pageY : this.startPosY;
  }

  touchStart(event) {
    this.isDraggingWasDetected = false;
    this.clickBlockUntil = 0;

    if (this.isInteractiveElement(event.target)) {
      this.isDragging = false;
      return;
    }

    this.stopAutoplay();
    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.startPosY = this.getPositionY(event);
    this.animationID = requestAnimationFrame(this.animation.bind(this));

    this.track.classList.add('grabbing');
    this.track.classList.remove('hero-slider__track--transition');

    this.startTime = Date.now();
  }

  touchMove(event) {
    if (!this.isDragging) return;

    const currentPosition = this.getPositionX(event);
    const currentPositionY = this.getPositionY(event);
    const diffX = currentPosition - this.startPos;
    const diffY = currentPositionY - this.startPosY;
    const isHorizontalSwipe = Math.abs(diffX) > this.dragThreshold && Math.abs(diffX) > Math.abs(diffY);

    if (isHorizontalSwipe) {
      this.isDraggingWasDetected = true;
      this.currentTranslate = this.prevTranslate + diffX;

      if (event.cancelable) {
        event.preventDefault();
      }
    }
  }

  touchEnd() {
    if (!this.isDragging) {
      this.isDraggingWasDetected = false;
      return;
    }

    const wasDragging = this.isDraggingWasDetected;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.track.classList.remove('grabbing');

    const movedBy = wasDragging ? this.currentTranslate - this.prevTranslate : 0;
    const duration = Math.max(Date.now() - this.startTime, 1);
    const velocity = Math.abs(movedBy) / duration;

    const threshold = this.slideWidth * 0.2;
    const flickVelocity = 0.3;

    if (movedBy < -threshold || (movedBy < -20 && velocity > flickVelocity)) {
      this.currentIndex += 1;
    } else if (movedBy > threshold || (movedBy > 20 && velocity > flickVelocity)) {
      this.currentIndex -= 1;
    }

    if (wasDragging) {
      this.clickBlockUntil = Date.now() + 350;
      window.setTimeout(() => {
        if (Date.now() >= this.clickBlockUntil) {
          this.isDraggingWasDetected = false;
          this.clickBlockUntil = 0;
        }
      }, 400);
    } else {
      this.isDraggingWasDetected = false;
      this.clickBlockUntil = 0;
    }

    this.setPositionByIndex();
    if (this.autoplayEnabled && !this.matches(':hover')) this.startAutoplay();
  }

  animation() {
    this.setSliderPosition();
    if (this.isDragging) requestAnimationFrame(this.animation.bind(this));
  }

  setSliderPosition() {
    this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
  }

  setPositionByIndex() {
    if (this.currentIndex < 0) this.currentIndex = this.infinite ? this.slides.length - 1 : 0;
    if (this.currentIndex >= this.slides.length) this.currentIndex = this.infinite ? 0 : this.slides.length - 1;

    this.currentTranslate = this.currentIndex * -this.slideWidth;
    this.prevTranslate = this.currentTranslate;

    this.track.classList.add('hero-slider__track--transition');
    this.setSliderPosition();
    this.updateDots();
    this.updateSlides();
  }

  updateSlides() {
    this.slides.forEach((slide, index) => {
      if (index === this.currentIndex) {
        slide.classList.add('is-active');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }

  prevSlide() {
    this.currentIndex -= 1;
    this.setPositionByIndex();
  }

  nextSlide() {
    this.currentIndex += 1;
    this.setPositionByIndex();
  }

  updateDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('active');
        dot.removeAttribute('aria-current');
      }
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
